import { Box, forwardRef, GridItem, GridProps, SimpleGrid, Tooltip } from '@eigtech/ui-shared-dave'
import { Button } from '@eigtech/ui/components/button'
import { useLocalVideo, useRemoteVideoTileState } from 'amazon-chime-sdk-component-library-react'
import { pick } from 'lodash-es'
import { Pin, PinOff } from 'lucide-react'
import { useShallow } from 'zustand/shallow'
import { MeetingState, useMeetingStore } from '../../context/MeetingState'
import { useFilteredRoster } from '../../hooks/useRoster'
import { DeprecatedVideoTile, DeprecatedVideoTileProps } from './DeprecatedVideoTile'
import { LocalVideo } from './LocalVideo'
import { RemoteVideo } from './RemoteVideo'

export type RosterVideosProps = GridProps

export const RosterVideos = forwardRef<RosterVideosProps, 'div'>((props, ref) => {
  const { chimeAttendee, selectedSpeaker, setSelectedSpeaker } = useMeetingStore(
    useShallow(stateSelector)
  )
  const roster = useFilteredRoster()

  const { isVideoEnabled } = useLocalVideo()

  const { attendeeIdToTileId } = useRemoteVideoTileState()

  return (
    <SimpleGrid ref={ref} columns={[2, 2, 3, 4, 5, 6]} gap={2} {...props}>
      {Object.values(roster).map((attendee) => {
        const tileId = attendeeIdToTileId[attendee.chimeAttendeeId]
        const isSelf = chimeAttendee?.AttendeeId === attendee.chimeAttendeeId
        const isPinned = selectedSpeaker === attendee.chimeAttendeeId

        const sharedProps: DeprecatedVideoTileProps = {
          attendeeId: attendee.chimeAttendeeId,
          name: attendee.name,
          actionsSlot: (
            <Tooltip label={`${isPinned ? 'Unpin' : 'Pin'} user`}>
              <Button
                aria-label={`${isPinned ? 'Unpin' : 'Pin'} user.`}
                className="size-[1.75em] text-white shadow-2xl"
                mode="icon"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedSpeaker(isPinned ? null : attendee.chimeAttendeeId)
                }}
              >
                {isPinned ? (
                  <Pin className="inherit size-[1em] text-[inherit]" />
                ) : (
                  <PinOff className="inherit size-[1em] text-[inherit]" />
                )}
              </Button>
            </Tooltip>
          ),
        }

        return (
          <GridItem
            key={attendee.chimeAttendeeId}
            cursor={isSelf ? undefined : 'pointer'}
            outline="solid"
            outlineColor={isPinned ? 'blue.300' : 'transparent'}
          >
            <Box>
              {isSelf && isVideoEnabled ? (
                <LocalVideo {...sharedProps} />
              ) : tileId >= 0 ? (
                <RemoteVideo {...sharedProps} tileId={tileId} />
              ) : (
                <DeprecatedVideoTile {...sharedProps}></DeprecatedVideoTile>
              )}
            </Box>
          </GridItem>
        )
      })}
    </SimpleGrid>
  )
})

const stateSelector = (state: MeetingState) =>
  pick(state, ['chimeAttendee', 'setSelectedSpeaker', 'selectedSpeaker'])
